import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";

// Inner sticker — defined at module scope so React's reconciler keeps the
// same component identity across re-renders (avoids `no-unstable-nested-components`).
function Sticker({ patient, hospital, branch, doctor, dateStr, timeStr }) {
  // Compact "32y / M" form — same convention as the IPD sticker.
  const ageSex = [
    patient.age != null && patient.age !== "" ? `${patient.age}y` : "",
    (patient.gender || "")[0]?.toUpperCase(),
  ].filter(Boolean).join(" / ") || "—";
  return (
    <div className="opd-sticker">
      <div className="opd-sticker-head">
        <div className="opd-sticker-brand">{(hospital?.name || branch?.name || "CareO Eye").toUpperCase()}</div>
        <div className="opd-sticker-type">OPD</div>
      </div>
      <div className="opd-sticker-row opd-sticker-row-half">
        <div><span className="opd-sticker-key">UHID</span><span className="opd-sticker-val opd-sticker-uhid">{patient.uhid || "—"}</span></div>
        <div><span className="opd-sticker-key">Age/Sex</span><span className="opd-sticker-val">{ageSex}</span></div>
      </div>
      <div className="opd-sticker-row">
        <span className="opd-sticker-key">Name</span>
        <span className="opd-sticker-val">{patient.name || "—"}</span>
      </div>
      <div className="opd-sticker-row opd-sticker-row-half">
        <div><span className="opd-sticker-key">Date</span><span className="opd-sticker-val">{dateStr}</span></div>
        <div><span className="opd-sticker-key">Time</span><span className="opd-sticker-val">{timeStr}</span></div>
      </div>
      <div className="opd-sticker-row">
        <span className="opd-sticker-key">Mobile</span>
        <span className="opd-sticker-val">{patient.mobile || "—"}</span>
      </div>
      <div className="opd-sticker-row">
        <span className="opd-sticker-key">Doctor</span>
        <span className="opd-sticker-val">{doctor}</span>
      </div>
    </div>
  );
}

/**
 * OPD sticker — a small, label-printer friendly tag stuck to the patient's
 * file / paperwork on registration day. Default size is 4" × 2" (about
 * 100mm × 50mm) which fits most Zebra / TSC desktop label printers, but
 * also looks fine on A6 or plain paper.
 *
 * Fields requested by the hospital:
 *   • UHID         — patient's branch-scoped unique ID
 *   • Patient name
 *   • Date         — today (registration date)
 *   • Time         — registration time (HH:MM)
 *   • Mobile no.
 *   • Doctor name  — passed via ?doctor=NAME query param; falls back to "—"
 *
 * URL: `/print/opd-sticker/<patientId>?doctor=<name>&copies=<n>`
 * Auto-prints unless `?autoprint=0` is set.
 */
export default function PrintOpdSticker() {
  const { patientId } = useParams();
  const [search] = useSearchParams();
  const doctor = search.get("doctor") || "—";
  const copies = Math.max(1, Math.min(4, Number(search.get("copies")) || 1));
  const autoprint = search.get("autoprint") !== "0";

  const [patient, setPatient] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [branch, setBranch] = useState(null);

  useEffect(() => {
    (async () => {
      const me = await api.get("/auth/me");
      setHospital(me.data.hospital);
      setBranch(me.data.branch);
      const { data } = await api.get(`/patients/${patientId}`);
      setPatient(data.patient || data);
      if (autoprint) setTimeout(() => window.print(), 500);
    })();
  }, [patientId, autoprint]);

  if (!patient) return <div className="p-8 text-sm text-ink-muted">Loading sticker…</div>;

  // Use the patient's `created_at` (registration time) if available — falls
  // back to "now" for older records where it's missing.
  const ts = patient.created_at ? new Date(patient.created_at) : new Date();
  const dateStr = ts.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  const timeStr = ts.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });

  return (
    <>
      <style>{`
        @page { size: 100mm 50mm; margin: 0; }
        @media print {
          body { background: white; }
          .no-print { display: none !important; }
        }
        body { background: #f5f5f5; }
        .opd-sticker {
          width: 100mm;
          height: 50mm;
          padding: 3mm 4mm;
          box-sizing: border-box;
          font-family: 'Calibri', 'Carlito', 'Helvetica', 'Arial', sans-serif;
          color: #111;
          page-break-after: always;
          background: white;
          display: flex;
          flex-direction: column;
          justify-content: space-between;
        }
        .opd-sticker-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1.2px solid #000;
          padding-bottom: 1.5mm;
          margin-bottom: 1.5mm;
        }
        .opd-sticker-brand {
          font-weight: 700;
          font-size: 10pt;
          letter-spacing: 0.3px;
        }
        .opd-sticker-type {
          font-weight: 800;
          font-size: 10pt;
          background: #000;
          color: #fff;
          padding: 0.5mm 2.5mm;
          border-radius: 1.5mm;
        }
        .opd-sticker-row {
          display: flex;
          align-items: baseline;
          gap: 2mm;
          font-size: 8.5pt;
          line-height: 1.15;
          margin: 0.4mm 0;
        }
        .opd-sticker-row-half {
          gap: 6mm;
        }
        .opd-sticker-row-half > div { display: flex; align-items: baseline; gap: 1.5mm; }
        .opd-sticker-key {
          font-size: 6.5pt;
          font-weight: 700;
          color: #555;
          letter-spacing: 0.4px;
          text-transform: uppercase;
          min-width: 13mm;
        }
        .opd-sticker-val {
          font-weight: 600;
          font-size: 9pt;
        }
        .opd-sticker-uhid {
          font-family: 'Courier New', monospace;
          font-size: 11pt;
          font-weight: 800;
        }
      `}</style>
      {Array.from({ length: copies }).map((_, i) =>
        <Sticker key={i} patient={patient} hospital={hospital} branch={branch} doctor={doctor} dateStr={dateStr} timeStr={timeStr} />
      )}
      <div className="no-print" style={{ position: "fixed", top: 8, right: 8 }}>
        <button onClick={() => window.print()} style={{ padding: "6px 12px", background: "#111", color: "#fff", borderRadius: 6, fontSize: 12, border: 0, cursor: "pointer" }} data-testid="opd-sticker-print-btn">
          Print sticker
        </button>
      </div>
    </>
  );
}
